// ログイン後の戻り先（`?redirect_uri=`）を、遷移してよい URL に限る。
//
// 検証しないまま `window.location.href` に入れると、`javascript:` が実行される
// （SpearlyToken は JS から読める Cookie なので、細工したログイン URL を踏ませるだけで
// ログイン直後のトークンを読み出せる）。外部ドメインへの open redirect にもなる。
//
// 通すもの:
//   1. このアプリと同じ origin（相対パス `/profile` など。既存の既定値がこれ）
//   2. https で、ホストが下の ALLOWED_HOSTS のどれか（ポートは問わない）
//   3. REACT_APP_CMS_URL / REACT_APP_CLOUD_URL と scheme・ホストが同じもの（ローカル開発用）
// それ以外は既定の遷移先にする。返すのは検証に使った URL を正規化したもの。
//
// **ドメインのサフィックスでは判定しない。** `*.spearly.app` は Spearly CLOUD の顧客サイトの
// 置き場所（cloud-api の SiteContext が `https://#{url_slug}.spearly.app` を作る）で、
// 誰でも中身を置ける。サフィックスで通すと、ログイン直後に顧客サイト上の偽ページへ飛ばせる。

export const DEFAULT_REDIRECT_AFTER_LOGIN = "/profile"

// ログイン後に戻ってよい Spearly のアプリだけを列挙する（2026-10 時点）。
// 出どころ: cms-api の APP_HOST（spearly-terraform cms/ecs.tf）、spearly-front-all の LP の
// ログインリンク、spearly-terraform の Route53 / ELB の CNAME、各ホストの実応答。
// 顧客サイトのホスト（`<slug>.spearly.app`）は入れないこと。
const ALLOWED_HOSTS = [
  // production
  "accounts.spearly.app",
  "cms.spearly.app",
  "cloud.spearly.app",
  "cloud.spearly.com",
  "workbench.spearly.app",
  "colorme.spearly.app",
  // staging
  "accounts.spearly.xyz",
  "cms.spearly.xyz",
  "cloud.spearly.xyz",
  "staging-cloud.spearly.com",
  "workbench.spearly.xyz",
  "colorme.spearly.xyz",
]

const envServiceUrls = (): URL[] =>
  [process.env.REACT_APP_CMS_URL, process.env.REACT_APP_CLOUD_URL].flatMap(
    (value) => {
      if (!value) return []
      try {
        return [new URL(value)]
      } catch {
        return []
      }
    },
  )

export const safeRedirectUri = (
  value: string | null | undefined,
  fallback: string = DEFAULT_REDIRECT_AFTER_LOGIN,
): string => {
  if (!value) return fallback

  let url: URL
  try {
    url = new URL(value, window.location.origin)
  } catch {
    return fallback
  }

  if (url.origin === window.location.origin) return url.href

  if (url.protocol === "https:" && ALLOWED_HOSTS.includes(url.hostname)) {
    return url.href
  }

  const matchesEnvService = envServiceUrls().some(
    (service) =>
      service.protocol === url.protocol && service.hostname === url.hostname,
  )
  if (matchesEnvService) return url.href

  return fallback
}
